export enum BREAK_TYPE {
  WORD = "word",
  SENTENCE = "sentence"
}

// main logic
const createWordSpans = (text: string, breakType: BREAK_TYPE = BREAK_TYPE.WORD) => {
  let regex = /(\n+)/;
  switch (breakType) {
    case BREAK_TYPE.SENTENCE:
      regex = /(\n+)/;
  }

  return text
    .split(regex)
    .map((str, idx) => {
      if (!str) return null;

      if (idx % 2) {
        return document.createTextNode(str);
      } else {
        const span = document.createElement("span");
        span.textContent = str;

        return span;
      }
    })
    .filter(Boolean);
};

export const measureTextHeight = (text: string, width: string, _element?: Partial<CSSStyleDeclaration>, styleClass?: string) => {
  const container = document.createElement("div");
  container.innerHTML = text;
  container.style.maxWidth = width;
  container.style.width = width;
  container.style.minWidth = width;
  
  // Voeg eerst de styleClass toe om de CSS regels te krijgen
  if (styleClass) container.classList.add(styleClass);
  document.body.appendChild(container);
  
  // Haal de computed styles op van het element met de juiste CSS class
  const computedStyle = window.getComputedStyle(container);
  
  // Force alle CSS eigenschappen exact hetzelfde voor consistentie
  container.style.fontFamily = computedStyle.fontFamily || '"Roboto", sans-serif';
  container.style.fontSize = computedStyle.fontSize || '16px'; // Gebruik computed font size
  container.style.fontWeight = computedStyle.fontWeight || 'normal';
  container.style.fontStyle = computedStyle.fontStyle || 'normal';
  container.style.lineHeight = computedStyle.lineHeight || 'normal';
  container.style.letterSpacing = computedStyle.letterSpacing || 'normal';
  container.style.wordSpacing = computedStyle.wordSpacing || 'normal';
  container.style.textAlign = computedStyle.textAlign || 'left';
  container.style.visibility = 'hidden'; // Voorkom flikkering
  container.style.boxSizing = 'border-box';
  container.style.padding = '0';
  container.style.margin = '0';
  container.style.border = 'none';
  container.style.outline = 'none';
  
  // Pas custom element styles toe als ze bestaan
  if (_element) {
    Object.entries(_element).forEach(([key, value]) => {
      if (value !== undefined) {
        container.style.setProperty(key, value as string);
      }
    });
  }

  // Wacht even om zeker te zijn dat fonts geladen zijn
  const height = container.getBoundingClientRect().height;
  
  document.body.removeChild(container);

  return height;
};

export const paginateTextBySize = (width: number, height: number, _style?: Partial<CSSStyleDeclaration>, styleClass?: string, breakType?: BREAK_TYPE) => {
  return (text: string) => {
    const cloneElement = document.createElement("div") as HTMLDivElement;
    cloneElement.style.minWidth = width + "px";
    cloneElement.style.maxWidth = width + "px";
    cloneElement.style.width = width + "px";
    
    // Voeg eerst de styleClass toe om de CSS regels te krijgen
    if (styleClass) cloneElement.classList.add(styleClass);
    cloneElement.style.color = "white";
    document.body.appendChild(cloneElement);
    
    // Haal de computed styles op van het element met de juiste CSS class
    const computedStyle = window.getComputedStyle(cloneElement);
    
    // Force alle CSS eigenschappen exact hetzelfde voor consistentie, gebruik computed values
    cloneElement.style.fontFamily = computedStyle.fontFamily || '"Roboto", sans-serif';
    cloneElement.style.fontSize = computedStyle.fontSize || '16px';
    cloneElement.style.fontWeight = computedStyle.fontWeight || 'normal';
    cloneElement.style.fontStyle = computedStyle.fontStyle || 'normal';
    cloneElement.style.lineHeight = computedStyle.lineHeight || 'normal';
    cloneElement.style.letterSpacing = computedStyle.letterSpacing || 'normal';
    cloneElement.style.wordSpacing = computedStyle.wordSpacing || 'normal';
    cloneElement.style.textAlign = computedStyle.textAlign || 'left';
    cloneElement.style.visibility = 'hidden'; // Voorkom flikkering
    cloneElement.style.boxSizing = 'border-box';
    cloneElement.style.padding = '0';
    cloneElement.style.margin = '0';
    cloneElement.style.border = 'none';
    cloneElement.style.outline = 'none';
    
    // Pas custom styles toe als ze bestaan
    if (_style) {
      Object.entries(_style).forEach(([key, value]) => {
        if (value !== undefined) {
          cloneElement.style.setProperty(key, value as string);
        }
      });
    }
    
    const res = paginateByBoundingElement(cloneElement, 0, height, breakType)(text);
    cloneElement.remove();
    return res;
  };
};

export const paginateTextByElement = (parent: HTMLDivElement, paddingY: number, breakType?: BREAK_TYPE) => {
  return paginateTextBySize(parent.clientWidth, parent.clientHeight - paddingY, parent.style, parent.className, breakType);
};

// note that this rewrites the content of the element
export const paginateByBoundingElement = (parent: HTMLDivElement, _paddingY: number, maxHeight: number, breakType?: BREAK_TYPE) => (text: string) => {
  // Parse html string to DOM
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, "text/html");
  const { body } = doc;

  // Create a empty element to fill with the content
  let nodes: (Node | ChildNode)[] = Array.from(body.childNodes);
  nodes = nodes
    .map((node) => {
      if (node instanceof Text) {
        return createWordSpans(node.textContent ?? "", breakType) as Node[];
      }
      return node;
    })
    .flat();
  
  const pages: {content: string, height: number}[] = [];
  
  for (const child of nodes) {
    parent.appendChild(child);
    const currentHeight = parent.getBoundingClientRect().height;
    
    if (currentHeight > maxHeight) {
      parent.removeChild(child);
      const pageHeight = parent.getBoundingClientRect().height;
      pages.push({content: parent.innerHTML, height: pageHeight});
      
      parent.textContent = "";
      parent.appendChild(child);
    }
  }
  
  const finalHeight = parent.getBoundingClientRect().height;
  pages.push({content: parent.innerHTML, height: finalHeight});
  
  return pages;
};
